/**
 * sessionStorage persistence for in-progress Pay Component Import wizard state (#228).
 *
 * Survives a full page refresh so Map Columns / Review can resume with the same
 * CSV, dry-run result, and options. Best-effort: storage may be unavailable or
 * full (large CSVs). Commit resume uses a separate key (`commitProgress.ts`).
 */

import { z } from "zod";
import { MyPayEnvironment } from "../../generated/core-api";
import { PAY_COMPONENT_IMPORT_BASE_PATH } from "../config";
import { MappingEntrySchema } from "../types/mapping";
import { StoredImportResultSchema } from "../types/import-result";
import { INITIAL_WIZARD_STATE, type WizardState } from "../types/wizard-state";
import { csvTextToFile } from "./csvTextToFile";

const STORAGE_KEY = "pci-wizard-session";

const MyPayEnvironmentSchema = z.nativeEnum(MyPayEnvironment);

const DuplicateHeaderGroupSchema = z.object({
  name: z.string(),
  count: z.number(),
});

const CsvPreviewSchema = z.object({
  headers: z.array(z.string()),
  rows: z.array(z.record(z.string(), z.string())),
  componentNames: z.array(z.string()),
  componentColumn: z.string().nullable(),
  duplicateHeaders: z.array(DuplicateHeaderGroupSchema),
});

const SerializableWizardStateSchema = z.object({
  csvText: z.string(),
  preview: CsvPreviewSchema,
  providerId: z.string().nullable(),
  mypayEnvironment: MyPayEnvironmentSchema,
  componentColumn: z.string().nullable(),
  startDateColumn: z.string().nullable().default(null),
  endDateColumn: z.string().nullable().default(null),
  importResult: StoredImportResultSchema.nullable(),
  /** Whether `importResult` is stale vs current mappings / date columns. */
  validationStale: z.boolean().default(false),
  pendingMappings: z.array(MappingEntrySchema),
  componentResolutions: z.record(z.string(), z.string()),
  pageSize: z.number(),
  failedRowsCsv: z.string().nullable(),
});

export const WizardSessionRecordSchema = z.object({
  companyCode: z.string(),
  companyName: z.string(),
  mypayEnvironment: MyPayEnvironmentSchema,
  lastRoute: z.string(),
  wizard: SerializableWizardStateSchema,
});

export type WizardSessionRecord = z.infer<typeof WizardSessionRecordSchema>;

const WIZARD_ROUTES = new Set([
  PAY_COMPONENT_IMPORT_BASE_PATH,
  `${PAY_COMPONENT_IMPORT_BASE_PATH}/map`,
  `${PAY_COMPONENT_IMPORT_BASE_PATH}/review`,
  `${PAY_COMPONENT_IMPORT_BASE_PATH}/errors`,
  `${PAY_COMPONENT_IMPORT_BASE_PATH}/results`,
]);

function getStore(): Storage | null {
  try {
    return typeof sessionStorage !== "undefined" ? sessionStorage : null;
  } catch {
    return null;
  }
}

export function isPersistableWizardRoute(pathname: string): boolean {
  return WIZARD_ROUTES.has(pathname);
}

/** Pick the wizard fields that can be JSON-serialized (excludes live File). */
export function toSerializableWizardState(
  state: WizardState,
): z.infer<typeof SerializableWizardStateSchema> | null {
  if (
    !state.preview ||
    !state.csvText ||
    state.mypayEnvironment === null ||
    state.importResult?.status === "committed"
  ) {
    return null;
  }
  return {
    csvText: state.csvText,
    preview: state.preview,
    providerId: state.providerId,
    mypayEnvironment: state.mypayEnvironment,
    componentColumn: state.componentColumn,
    startDateColumn: state.startDateColumn,
    endDateColumn: state.endDateColumn,
    importResult: state.importResult,
    validationStale: state.validationStale,
    pendingMappings: state.pendingMappings,
    componentResolutions: state.componentResolutions,
    pageSize: state.pageSize,
    failedRowsCsv: state.failedRowsCsv,
  };
}

export function hydrateWizardState(
  session: WizardSessionRecord,
  fallbackMypayEnvironment: MyPayEnvironment | null,
): WizardState {
  const w = session.wizard;
  const mypayEnvironment =
    w.mypayEnvironment ?? fallbackMypayEnvironment;
  return {
    file: csvTextToFile(w.csvText),
    csvText: w.csvText,
    preview: w.preview,
    providerId: w.providerId,
    mypayEnvironment,
    componentColumn: w.componentColumn,
    startDateColumn: w.startDateColumn,
    endDateColumn: w.endDateColumn,
    importResult: w.importResult,
    validationStale: w.validationStale,
    editedRowIndexes: [],
    pendingMappings: w.pendingMappings,
    componentResolutions: w.componentResolutions,
    pageSize: w.pageSize,
    failedRowsCsv: w.failedRowsCsv,
  };
}

export function saveWizardSession(record: WizardSessionRecord): void {
  const store = getStore();
  if (!store) return;
  try {
    store.setItem(STORAGE_KEY, JSON.stringify(record));
  } catch {
    // Storage full or disabled — resume is best-effort.
  }
}

export function loadWizardSession(): WizardSessionRecord | null {
  const store = getStore();
  if (!store) return null;
  let raw: string | null;
  try {
    raw = store.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const parsed = WizardSessionRecordSchema.parse(JSON.parse(raw));
    if (!isPersistableWizardRoute(parsed.lastRoute)) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function clearWizardSession(): void {
  const store = getStore();
  if (!store) return;
  try {
    store.removeItem(STORAGE_KEY);
  } catch {
    // Nothing actionable if removal fails.
  }
}

export function loadBoundCompanyFromSession(): {
  companyCode: string;
  companyName: string;
  mypayEnvironment: MyPayEnvironment;
} | null {
  const session = loadWizardSession();
  if (!session) return null;
  return {
    companyCode: session.companyCode,
    companyName: session.companyName,
    mypayEnvironment: session.mypayEnvironment,
  };
}

export function buildWizardSessionRecord(args: {
  companyCode: string;
  companyName: string;
  state: WizardState;
  lastRoute: string;
}): WizardSessionRecord | null {
  const wizard = toSerializableWizardState(args.state);
  if (!wizard || !isPersistableWizardRoute(args.lastRoute)) {
    return null;
  }
  return {
    companyCode: args.companyCode,
    companyName: args.companyName,
    mypayEnvironment: wizard.mypayEnvironment,
    lastRoute: args.lastRoute,
    wizard,
  };
}

export function tryRestoreWizardState(
  companyCode: string,
  mypayEnvironment: MyPayEnvironment | null,
): WizardState {
  const session = loadWizardSession();
  if (
    !session ||
    session.companyCode !== companyCode ||
    (mypayEnvironment !== null &&
      session.mypayEnvironment !== mypayEnvironment)
  ) {
    return {
      ...INITIAL_WIZARD_STATE,
      mypayEnvironment,
    };
  }
  return hydrateWizardState(session, mypayEnvironment);
}
